/**
 * The VR entry point's view of the world. The composition root hands over
 * the VRMode instance once the canvas exists; the button component renders
 * only if a headset is actually present - the project promises nothing is
 * gated behind hardware, so on a classroom machine with no headset there is
 * not even a control to wonder about.
 */

import type { WebGLRenderer } from 'three'
import { VRMode } from '../../xr/VRMode'

let vr: VRMode | null = null
let supported = false
const listeners = new Set<() => void>()

function notify(): void {
  for (const fn of [...listeners]) fn()
}

export const vrBridge = {
  init(mode: VRMode, gl: WebGLRenderer): void {
    vr = mode
    void VRMode.isSupported().then((ok) => {
      if (!ok) return
      supported = true
      notify()
    })
    // Session comings and goings re-render the button's label.
    gl.xr.addEventListener('sessionstart', notify)
    gl.xr.addEventListener('sessionend', notify)
  },
  get supported(): boolean {
    return supported
  },
  get active(): boolean {
    return vr?.active ?? false
  },
  async enter(): Promise<void> {
    await vr?.enter()
    notify()
  },
  exit(): void {
    vr?.exit()
    notify()
  },
  subscribe(fn: () => void): () => void {
    listeners.add(fn)
    return () => {
      listeners.delete(fn)
    }
  },
}
